import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Brand, ThemeToggle, Dawn, Split, Arrow } from "@/components/hm/Basics";
import { FluidLayer } from "@/components/hm/FluidHero";
import { useAuth } from "@/context/AuthContext";

const LINKS = [["/como-funciona", "Cómo funciona", "como"], ["/#respira", "Recursos", "respira"], ["/#planes", "Planes", "planes"], ["/#faq", "Preguntas", "faq"]];

export const LandingNav = () => {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 30);
    on(); addEventListener("scroll", on, { passive: true });
    return () => removeEventListener("scroll", on);
  }, []);
  return (
    <header className={`nav ${scrolled ? "scrolled" : ""}`}>
      <div className="wrap">
        <div className="nav-in">
          <Brand />
          <nav className="nav-links" aria-label="Principal">
            {LINKS.map(([h, l, id]) => <Link key={id} to={h} data-testid={`landing-nav-${id}`}>{l}</Link>)}
          </nav>
          <div className="nav-act">
            <ThemeToggle />
            {user ? (
              <Link to="/inicio" className="btn btn-primary btn-sm mag" data-testid="landing-go-app">Ir a mi espacio</Link>
            ) : (
              <>
                <Link to="/login" className="btn btn-ghost btn-sm hide-sm" data-testid="landing-login-link">Entrar</Link>
                <Link to="/modulo-bienvenida" className="btn btn-primary btn-hm btn-sm mag" data-testid="landing-nav-cta">Empezar ahora</Link>
              </>
            )}
            <button className="icon-btn nav-burger" onClick={() => setOpen((o) => !o)} aria-expanded={open}
              aria-label={open ? "Cerrar menú" : "Abrir menú"} data-testid="landing-mobile-menu-toggle">
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="nav-mobile" aria-label="Menú" data-testid="landing-mobile-menu" onClick={() => setOpen(false)}>
            {LINKS.map(([h, l, id]) => <Link key={id} to={h} data-testid={`mobile-nav-${id}`}>{l}</Link>)}
            {!user && <Link to="/login" data-testid="mobile-nav-login">Entrar</Link>}
          </nav>
        )}
      </div>
    </header>
  );
};

const TrustIcon = ({ d }) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d={d} /></svg>;

export const Hero = () => {
  const heroRef = useRef(null);
  const [hint, setHint] = useState(() => (matchMedia("(hover:hover)").matches ? "Mueve el cursor · despeja la niebla" : "Desliza el dedo · despeja la niebla"));
  const onFallback = useCallback(() => setHint("Para tu despertar"), []);
  return (
    <section className="hero" id="hero" ref={heroRef}>
      <Dawn />
      <FluidLayer heroRef={heroRef} onFallback={onFallback} />
      <div className="wrap hero-c">
        <div className="hero-row">
          <div>
            <Split as="h1" className="h-display" text="Para cuando te sientes vacío o desconectado" data-testid="landing-title" />
            <p className="hero-sub rv" style={{ "--d": "350ms" }} data-testid="landing-subtitle">Entiende lo que sientes. Recupera claridad.</p>
            <div className="lead hero-emo rv" style={{ "--d": "500ms" }} data-testid="landing-emotional-text">
              <p>Si últimamente te notas raro, sin energía, sin dirección o desconectado de ti, no estás solo.</p>
              <p>Lo que te pasa tiene sentido.</p>
              <p>Aquí puedes empezar a entenderte con calma, acompañado, y recuperar la claridad que ahora mismo no encuentras.</p>
            </div>
            <div className="hero-cta rv" style={{ "--d": "650ms" }}>
              <Link to="/modulo-bienvenida" className="btn btn-primary btn-hm mag" data-testid="landing-cta-hero">Empezar ahora <Arrow /></Link>
              <Link to="/como-funciona" className="btn btn-ghost" data-testid="landing-how-link">Ver cómo funciona</Link>
            </div>
            <div className="trust rv" style={{ "--d": "800ms" }}>
              <span><TrustIcon d="M20 6 9 17l-5-5" />Sin tarjeta para empezar</span>
              <span><TrustIcon d="M12 7v5l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" />8 minutos</span>
              <span><TrustIcon d="M8 11V8a4 4 0 0 1 8 0v3M5 11h14v10H5z" />Tu diario es solo tuyo</span>
            </div>
          </div>
          <aside className="teaser teaser-hm rv" style={{ "--d": "900ms" }} aria-label="Módulo de bienvenida" data-testid="landing-teaser">
            <div className="teaser-top" data-testid="landing-teaser-title"><span>Módulo de bienvenida • 8 min • gratis</span></div>
            <p style={{ margin: 0 }} data-testid="landing-teaser-text">
              Lo que sientes tiene sentido. No estás perdido: estás en un momento de cambio.<br />Este módulo te ayuda a entender por qué te sientes vacío o desconectado y a empezar a ordenar tu mundo interno con claridad.
            </p>
            <Link to="/modulo-bienvenida" className="btn btn-primary btn-hm btn-sm" data-testid="landing-teaser-cta">Empezar ahora <Arrow /></Link>
          </aside>
        </div>
        <div className="hero-foot">
          <span className="hint"><i /><span>{hint}</span></span>
          <span className="cue" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
};

const WORDS = ["No estás roto", "Estás cambiando", "Paso a paso", "Sin prisa", "Ponle nombre a lo que sientes", "Respira"];

export const Marquee = () => (
  <div className="marq" aria-hidden="true">
    <div className="marq-t">{[...WORDS, ...WORDS].map((w, i) => <span key={i}>{w}</span>)}</div>
  </div>
);

const MIRROR = [
  "Lo que antes te llenaba ahora te deja vacío.",
  "Notas más tus emociones… o incluso las de los demás.",
  "Algunas conversaciones te cansan más que antes.",
  "Necesitas más silencio. O más verdad.",
  "Y no sabes muy bien a quién contárselo.",
];

export const Mirror = () => {
  const ref = useRef(null);
  useEffect(() => {
    const io = new IntersectionObserver((es) => es.forEach((e) => e.target.classList.toggle("lit", e.isIntersecting)), { rootMargin: "-38% 0px -45% 0px" });
    ref.current.querySelectorAll("li").forEach((li) => io.observe(li));
    return () => io.disconnect();
  }, []);
  return (
    <section className="sec welcome-mod mirror-sec" style={{ marginBottom: 0, overflow: "clip" }} data-testid="landing-mirror">
      <div className="wrap mirror">
        <div className="sticky-col">
          <span className="eyebrow mirror-eyebrow" data-testid="mirror-title">Te suena</span>
          <Split className="hm-h1" style={{ marginTop: "1.2rem" }} text="Si algo de esto te pasa," serif="estás en el sitio adecuado." />
          <div className="lead rv mirror-intro" data-testid="mirror-intro">
            <p>Lo que vives tiene sentido.</p>
            <p>Es una señal de que tu interior está reorganizándose.</p>
            <p>Tu corazón está empezando a pedirte claridad.</p>
          </div>
        </div>
        <div>
          <ol className="mirror-list" ref={ref}>
            {MIRROR.map((m, i) => <li key={m}><span>0{i + 1}</span>{m}</li>)}
          </ol>
          <p className="mirror-close rv" data-testid="mirror-close">No estás solo. Esto tiene sentido. Y aquí puedes empezar a entenderlo.</p>
        </div>
      </div>
    </section>
  );
};
